import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import {
  X, Pencil, Monitor, Tablet, Smartphone,
  BarChart2, Calendar, Layers, Tag, Eye, MousePointerClick, Inbox,
} from 'lucide-react';

interface TemplatePreviewModalProps {
  template: any;
  onClose: () => void;
  onEdit: () => void;
}

// ── helpers ────────────────────────────────────────────────────────────────
const toDate = (input?: string | Date | null): Date | null => {
  if (!input) return null;
  if (input instanceof Date) return isNaN(input.getTime()) ? null : input;
  const str = String(input).trim();
  if (!str) return null;
  const iso = str.includes('T') ? str : str.replace(' ', 'T');
  const finalStr = iso.length === 19 ? `${iso}Z` : iso;
  const d = new Date(finalStr);
  return isNaN(d.getTime()) ? null : d;
};

const formatDate = (d: Date | null): string => {
  if (!d) return '—';
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

type CategoryKey = 'newsletter' | 'promotional' | 'transactional' | 'welcome' | 'announcement' | 'default';

const getCategoryColor = (cat: string): { badge: string; bar: string } => {
  const map: Record<CategoryKey, { badge: string; bar: string }> = {
    newsletter: { badge: 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400', bar: 'from-blue-500 to-indigo-500' },
    promotional: { badge: 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400', bar: 'from-emerald-400 to-teal-500' },
    transactional: { badge: 'bg-violet-50 dark:bg-violet-900/20 text-violet-700 dark:text-violet-400', bar: 'from-violet-500 to-purple-500' },
    welcome: { badge: 'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400', bar: 'from-amber-400 to-orange-500' },
    announcement: { badge: 'bg-rose-50 dark:bg-rose-900/20 text-rose-700 dark:text-rose-400', bar: 'from-rose-500 to-pink-500' },
    default: { badge: 'bg-gray-100 dark:bg-dark-700 text-content-secondary dark:text-gray-400', bar: 'from-blue-500 to-indigo-600' },
  };
  return map[(cat?.toLowerCase() as CategoryKey)] || map.default;
};

const getPreviewDimensions = (mode: 'desktop' | 'tablet' | 'mobile'): string => {
  if (mode === 'mobile') return 'max-w-[360px]';
  if (mode === 'tablet') return 'max-w-[600px]';
  return 'max-w-[720px]';
};

// ── component ──────────────────────────────────────────────────────────────
const TemplatePreviewModal: React.FC<TemplatePreviewModalProps> = ({ template, onClose, onEdit }) => {
  const [previewMode, setPreviewMode] = useState<'desktop' | 'tablet' | 'mobile'>('desktop');

  // derived values — preserved exactly
  const name: string = template?.name || 'Untitled Template';
  const description: string = template?.description || '';
  const thumbnail: string =
    (template?.thumbnail && String(template.thumbnail).trim()) ||
    (template?.thumbnail_url && String(template.thumbnail_url).trim()) || '';
  const category: string = template?.category || template?.tag || 'Default';
  const usageCount: number = Array.isArray(template?.temp_used_in_campaign)
    ? template.temp_used_in_campaign.length
    : (template?.performance?.usageCount ?? 0);
  const openRate: string = template?.performance?.openRate ?? '—';
  const clickRate: string = template?.performance?.clickRate ?? '—';
  const version: string | number = template?.metadata?.version ?? template?.version ?? 1;
  const blockCount: number = template?.metadata?.blockCount ?? (Array.isArray(template?.blocks) ? template.blocks.length : 0);
  const tags: string[] = Array.isArray(template?.tags) ? template.tags : [];
  const createdDate = toDate(template?.metadata?.createdAt ?? template?.createdDate);

  const catColors = getCategoryColor(category);
  const widthClass = getPreviewDimensions(previewMode);

  // keyboard close
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  // lock body scroll
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  const deviceButtons: { key: 'desktop' | 'tablet' | 'mobile'; Icon: typeof Monitor; label: string }[] = [
    { key: 'desktop', Icon: Monitor, label: 'Desktop' },
    { key: 'tablet', Icon: Tablet, label: 'Tablet' },
    { key: 'mobile', Icon: Smartphone, label: 'Mobile' },
  ];

  const modal = (
    <div
      className="fixed inset-0 z-[99999] flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0,0,0,0.55)' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      {/* Blur backdrop */}
      <div className="absolute inset-0 backdrop-blur-sm" />

      {/* Modal shell */}
      <div
        className="relative z-10 w-full max-w-4xl max-h-[92vh] flex flex-col
          bg-surface dark:bg-dark-800
          border border-line dark:border-dark-700
          rounded-2xl shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* ── Gradient accent bar ── */}
        <div className={`absolute top-0 inset-x-0 h-[2px] bg-gradient-to-r ${catColors.bar}`} />

        {/* ── Header ── */}
        <div className="flex items-center gap-3 px-5 pt-5 pb-4 border-b border-line-subtle dark:border-dark-700 flex-shrink-0">
          {/* Title block */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-title-sm font-bold text-content dark:text-white truncate">
                {name}
              </h2>
              <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wide ${catColors.badge}`}>
                {category}
              </span>
              {createdDate && (
                <span className="hidden sm:flex items-center gap-1 text-[11px] text-content-subtle dark:text-gray-500">
                  <Calendar className="w-3 h-3" />
                  {formatDate(createdDate)}
                </span>
              )}
            </div>
            {description && (
              <p className="mt-0.5 text-meta text-content-muted dark:text-gray-400 truncate">
                {description}
              </p>
            )}
          </div>

          {/* Device toggle pill */}
          <div className="hidden sm:flex items-center gap-0.5 bg-gray-100 dark:bg-dark-700 rounded-xl p-1">
            {deviceButtons.map(({ key, Icon, label }) => (
              <button
                key={key}
                onClick={() => setPreviewMode(key)}
                title={label}
                className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-semibold transition-all
                  ${previewMode === key
                    ? 'bg-surface dark:bg-dark-600 text-content dark:text-white shadow-sm'
                    : 'text-content-muted dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                  }`}
              >
                <Icon className="w-3.5 h-3.5" />
                <span className="hidden md:inline">{label}</span>
              </button>
            ))}
          </div>

          {/* Action buttons */}
          <button
            onClick={onEdit}
            className="flex items-center gap-1.5 h-9 px-4 rounded-xl text-[13px] font-semibold
              bg-gradient-to-r from-blue-600 to-indigo-600 text-white
              hover:from-blue-700 hover:to-indigo-700 transition-all shadow-sm shadow-blue-500/20"
          >
            <Pencil className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Edit</span>
          </button>

          <button
            onClick={onClose}
            className="w-9 h-9 flex items-center justify-center rounded-xl
              text-content-subtle dark:text-gray-500
              hover:text-gray-700 dark:hover:text-gray-200
              hover:bg-gray-100 dark:hover:bg-dark-700
              transition-colors border border-transparent hover:border-line dark:hover:border-dark-600"
            aria-label="Close"
          >
            <X className="w-4.5 h-4.5" />
          </button>
        </div>

        {/* ── Mobile device toggle ── */}
        <div className="sm:hidden flex items-center gap-0.5 mx-5 mt-3 mb-0 bg-gray-100 dark:bg-dark-700 rounded-xl p-1">
          {deviceButtons.map(({ key, Icon, label }) => (
            <button
              key={key}
              onClick={() => setPreviewMode(key)}
              className={`flex-1 flex items-center justify-center gap-1 py-1.5 rounded-lg text-[11px] font-semibold transition-all
                ${previewMode === key
                  ? 'bg-surface dark:bg-dark-600 text-content dark:text-white shadow-sm'
                  : 'text-content-muted dark:text-gray-400'
                }`}
            >
              <Icon className="w-3.5 h-3.5" />
              {label}
            </button>
          ))}
        </div>

        {/* ── Preview body ── */}
        <div className="flex-1 overflow-y-auto bg-gray-50 dark:bg-[#0e1117] flex items-start justify-center p-6 min-h-0">
          <div className={`w-full ${widthClass} transition-all duration-300`}>
            {/* Browser chrome mockup */}
            <div className="bg-surface dark:bg-dark-800 rounded-xl border border-line dark:border-dark-700 shadow-lg overflow-hidden">
              {/* Email content */}
              {thumbnail ? (
                <img
                  src={thumbnail}
                  alt={`${name} preview`}
                  className="w-full h-auto rounded-lg border border-line-subtle dark:border-dark-700"
                />
              ) : (
                <div>
                  {/* Mock email header */}
                  <div className="text-center mb-5 pb-5 border-b border-line-subtle dark:border-dark-700">
                    <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-500 mb-3">
                      <Inbox className="w-6 h-6 text-white" />
                    </div>
                    <div className="h-3 bg-gray-100 dark:bg-dark-700 rounded-full w-40 mx-auto mb-2 animate-pulse" />
                    <div className="h-2 bg-gray-100 dark:bg-dark-700 rounded-full w-24 mx-auto animate-pulse" />
                  </div>
                  {/* Mock content lines */}
                  <div className="space-y-2 mb-5">
                    {[80, 65, 90, 55, 75].map((w, i) => (
                      <div key={i} className="h-2 bg-gray-100 dark:bg-dark-700 rounded-full animate-pulse" style={{ width: `${w}%` }} />
                    ))}
                  </div>
                  {/* Mock CTA */}
                  <div className="flex justify-center mb-5">
                    <div className="h-9 w-32 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-xl opacity-60" />
                  </div>
                  {/* Mock footer */}
                  <div className="text-center pt-4 border-t border-line-subtle dark:border-dark-700 space-y-1">
                    <div className="h-2 bg-gray-100 dark:bg-dark-700 rounded-full w-36 mx-auto animate-pulse" />
                    <div className="h-1.5 bg-gray-100 dark:bg-dark-700 rounded-full w-24 mx-auto animate-pulse" />
                  </div>
                </div>
              )}

            </div>
          </div>
        </div>

        {/* ── Info footer bar ── */}
        <div className="flex-shrink-0 border-t border-line-subtle dark:border-dark-700 bg-gray-50/80 dark:bg-dark-700/40 px-5 py-3">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">

            {/* Performance */}
            <div>
              <div className="flex items-center gap-1.5 mb-1.5">
                <BarChart2 className="w-3 h-3 text-blue-500 dark:text-blue-400" />
                <span className="text-[10px] font-bold uppercase tracking-wider text-content-muted dark:text-gray-400">
                  Performance
                </span>
              </div>
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-1 text-[11px]">
                  <Inbox className="w-3 h-3 text-content-subtle" />
                  <span className="font-semibold text-gray-800 dark:text-gray-200">{usageCount}</span>
                  <span className="text-content-muted dark:text-gray-400">used</span>
                </div>
                <div className="flex items-center gap-1 text-[11px]">
                  <Eye className="w-3 h-3 text-content-subtle" />
                  <span className="font-semibold text-gray-800 dark:text-gray-200">{openRate}</span>
                  <span className="text-content-muted dark:text-gray-400">open</span>
                </div>
                <div className="flex items-center gap-1 text-[11px]">
                  <MousePointerClick className="w-3 h-3 text-content-subtle" />
                  <span className="font-semibold text-gray-800 dark:text-gray-200">{clickRate}</span>
                  <span className="text-content-muted dark:text-gray-400">CTR</span>
                </div>
              </div>
            </div>

            {/* Details */}
            <div>
              <div className="flex items-center gap-1.5 mb-1.5">
                <Layers className="w-3 h-3 text-violet-500 dark:text-violet-400" />
                <span className="text-[10px] font-bold uppercase tracking-wider text-content-muted dark:text-gray-400">
                  Details
                </span>
              </div>
              <div className="flex items-center gap-3 text-[11px]">
                {createdDate && (
                  <div className="flex items-center gap-1">
                    <Calendar className="w-3 h-3 text-content-subtle" />
                    <span className="text-content-muted dark:text-gray-400">{formatDate(createdDate)}</span>
                  </div>
                )}
                <div className="flex items-center gap-1">
                  <span className="text-content-muted dark:text-gray-400">v{version}</span>
                </div>
                {blockCount > 0 && (
                  <div className="flex items-center gap-1">
                    <Layers className="w-3 h-3 text-content-subtle" />
                    <span className="text-content-muted dark:text-gray-400">{blockCount} blocks</span>
                  </div>
                )}
              </div>
            </div>

            {/* Tags */}
            <div>
              <div className="flex items-center gap-1.5 mb-1.5">
                <Tag className="w-3 h-3 text-emerald-500 dark:text-emerald-400" />
                <span className="text-[10px] font-bold uppercase tracking-wider text-content-muted dark:text-gray-400">
                  Tags
                </span>
              </div>
              {tags.length > 0 ? (
                <div className="flex flex-wrap gap-1">
                  {tags.slice(0, 5).map((tag, i) => (
                    <span
                      key={i}
                      className="px-1.5 py-0.5 rounded-md bg-gray-100 dark:bg-dark-700 text-content-secondary dark:text-gray-400 text-[10px] font-medium"
                    >
                      {tag}
                    </span>
                  ))}
                  {tags.length > 5 && (
                    <span className="px-1.5 py-0.5 rounded-md bg-gray-100 dark:bg-dark-700 text-content-muted dark:text-gray-500 text-[10px] font-medium">
                      +{tags.length - 5}
                    </span>
                  )}
                </div>
              ) : (
                <span className="text-[11px] text-content-subtle dark:text-gray-500">No tags</span>
              )}
            </div>

          </div>
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modal, document.body);
};

export default TemplatePreviewModal;
